import { useMemo } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import type { ItemImage } from 'root/apiTypes';
import { useGetDisplayStylesParams } from 'root/hooks/useGetDisplayStylesParams';
import type { ItemProps } from './Item';
import { isSectionChecked, isPriceShowed } from 'root/utils/display';
import { PricingType } from 'root/clientTypes';
import type { MenuCheckbox } from 'root/clientTypes';
import { useGetDisplaySettingsParams } from 'root/hooks/useGetDisplaySettingsParams';
import { useItemsContext } from 'root/contexts/ItemsContext';
import { useGetHtmlTag } from 'root/hooks/useGetHtmlTag';
import { useSettings } from '@wix/tpa-settings/react';
import { DesignSettingsParams } from 'root/components/Menus/Settings/Tabs/DesignTab/DesignSettingsParams';

export const useItemLogic = ({ item }: ItemProps) => {
  const { sectionId, discountInfoByItemId } = useItemsContext();
  const discountInfo = item.id ? discountInfoByItemId?.[item.id] : undefined;
  const { showItemCurrency } = useGetDisplayStylesParams();
  const { t } = useTranslation();
  const isMarketPrice = item.pricingType === PricingType.MARKET_PRICE;

  const {
    checkPricingMenus,
    checkPlaceholderMenu,
    imagePlaceholder,
    showZeroPrice,
    showZeroPriceToAll,
    showImagePlaceholder,
    showPlaceholderToAll,
  } = useGetDisplaySettingsParams();

  const itemTitleHtmlTag = useGetHtmlTag('itemTitleTheme');
  const itemDescriptionHtmlTag = useGetHtmlTag('itemDescriptionTheme');
  const itemPriceHtmlTag = useGetHtmlTag('itemPriceTheme');

  const settings = useSettings();

  const hasPriceVariants = (item.priceVariants?.variants ?? []).length > 0;

  const priceOptionSectionChecked =
    showZeroPriceToAll || isSectionChecked(checkPricingMenus as MenuCheckbox[], sectionId ?? '');

  const priceContent = isMarketPrice
    ? t('app.item.market-price')
    : showItemCurrency
    ? item.priceInfo?.formattedPrice
    : item.priceInfo?.formattedPriceNoCurrency ?? '';

  const shouldShowPrice =
    isMarketPrice ||
    isPriceShowed({
      price: item.priceInfo?.price ?? '',
      showZeroPrice: showZeroPrice as boolean,
      sectionChecked: priceOptionSectionChecked as boolean,
    });

  const shouldShowLabels = item.labels && item.labels.length > 0;

  const imagePlaceholderSectionChecked =
    showPlaceholderToAll || isSectionChecked(checkPlaceholderMenu as MenuCheckbox[], sectionId ?? '');

  const shouldShowModifierGroups = item.modifierGroups && item.modifierGroups?.length > 0;
  const shouldShowImagePlaceholder = useMemo(
    () => (showImagePlaceholder ? imagePlaceholderSectionChecked : false),
    [imagePlaceholderSectionChecked, showImagePlaceholder]
  );

  const itemImage = useMemo(
    () => (item?.image ? item?.image : shouldShowImagePlaceholder ? imagePlaceholder : undefined) as ItemImage,
    [imagePlaceholder, item?.image, shouldShowImagePlaceholder]
  );

  const isFeaturedItem = item.featured;
  const shouldOverrideItemDesign = isFeaturedItem && settings.get(DesignSettingsParams.overrideItemDesign);

  const discountLabel = discountInfo && !discountInfo.hidePriceDisplay ? discountInfo.label : undefined;
  const discountedPrice = discountInfo && !discountInfo.hidePriceDisplay ? discountInfo.formattedFinalPrice : undefined;
  const variantDiscounts = discountInfo && !discountInfo.hidePriceDisplay ? discountInfo.variantDiscounts : undefined;

  return {
    hasPriceVariants,
    isMarketPrice,
    priceContent,
    shouldShowPrice,
    shouldShowLabels,
    shouldShowModifierGroups,
    shouldShowImagePlaceholder,
    itemImage,
    itemTitleHtmlTag,
    itemDescriptionHtmlTag,
    itemPriceHtmlTag,
    isFeaturedItem,
    shouldOverrideItemDesign,
    discountLabel,
    discountedPrice,
    variantDiscounts,
  };
};
